import React from 'react'
import { Well } from '@pushly/aqe/lib/components'

import { MutableAppMessageConditions } from '@pushly/models/lib/structs/app-messages/app-message-conditions'
import { AppMessageConditionType } from '@pushly/models/lib/enums/app-messages/app-message-condition-type'
import { getClassNames } from '../../../_utils/classnames'
import titleCase from 'title-case'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { PropertiesOf } from '@pushly/models/lib/types'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { convertSeconds, convertToSeconds } from '../../../_utils/time'
import { LIFETIME_METRICS, SESSION_METRICS_FILTERED } from '../constants'
import { ConditionDuration, SessionTriggerTypes } from '../types'
// import './styles/app-message-conditions-builder.scss'

import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { useState } from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Flex, Form, Input, Popover, Radio, Select, Skeleton, Space, Typography } from 'antd'

type AppMessageConditionsBuilderProps = {}

const TRIGGER_TYPE_DESCRIPTIONS: Record<AppMessageConditionType, string> = {
    [AppMessageConditionType.IMMEDIATE]: 'Shows immediately or the next time the app opens if it is currently closed.',
    [AppMessageConditionType.SESSION_START]:
        'Shows when a new session begins. For example, when the user opens the app after being away for long enough or after a cold app start.',
    [AppMessageConditionType.SESSION_TIME_IN_APP]:
        'Session Time in App shows after the user has spent the configured amount of time in their current session.',
    [AppMessageConditionType.LIFETIME_TIME_IN_APP]:
        'Lifetime Time in App shows after the user has spent the configured total time across all sessions.',
    [AppMessageConditionType.CUSTOM]: 'Shows once the defined custom trigger condition is met.',
}

const SESSION_TRIGGER_LABELS: Record<SessionTriggerTypes, string> = {
    [SessionTriggerTypes.IMMEDIATE]: 'Immediately',
    [SessionTriggerTypes.SESSION_START]: 'On session start',
    [SessionTriggerTypes.TIME_IN_APP]: 'After time in app',
    [SessionTriggerTypes.CUSTOM]: 'Custom trigger',
}

const SESSION_TRIGGER_OPTIONS = (Object.values(SessionTriggerTypes) as SessionTriggerTypes[]).map((value) => ({
    value,
    label: SESSION_TRIGGER_LABELS[value],
}))

const mapConditionToSessionTriggerType = (condition: AppMessageConditionType): SessionTriggerTypes | undefined => {
    switch (condition) {
        case AppMessageConditionType.SESSION_START:
            return SessionTriggerTypes.SESSION_START
        case AppMessageConditionType.IMMEDIATE:
            return SessionTriggerTypes.IMMEDIATE
        case AppMessageConditionType.SESSION_TIME_IN_APP:
        case AppMessageConditionType.LIFETIME_TIME_IN_APP:
            return SessionTriggerTypes.TIME_IN_APP
        case AppMessageConditionType.CUSTOM:
            return SessionTriggerTypes.CUSTOM
        default:
            return undefined
    }
}

const SESSION_TRIGGER_DESCRIPTION_MAP: Record<SessionTriggerTypes, string[]> = {
    [SessionTriggerTypes.IMMEDIATE]: [],
    [SessionTriggerTypes.SESSION_START]: [],
    [SessionTriggerTypes.TIME_IN_APP]: [],
    [SessionTriggerTypes.CUSTOM]: [],
}

;(Object.entries(TRIGGER_TYPE_DESCRIPTIONS) as [AppMessageConditionType, string][]).forEach(
    ([conditionType, description]) => {
        const sessionTriggerType = mapConditionToSessionTriggerType(conditionType)
        if (!sessionTriggerType) {
            return
        }

        SESSION_TRIGGER_DESCRIPTION_MAP[sessionTriggerType].push(description)
    },
)

export const AppMessageConditionsBuilder: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<AppMessageConditionsBuilderProps>>
> = (props) => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context

    const value = variants[selectedVariantIdx].schedules![0].conditions

    const localValue = value ?? new MutableAppMessageConditions()
    const [displayMetricOptions, setDisplayMetricOptions] = useState(SESSION_METRICS_FILTERED)
    const [plainTimeInAppSeconds, setPlainTimeInAppSeconds] = useState(
        !!localValue.timeInApp
            ? convertSeconds(localValue.timeInApp.intervalSeconds, localValue.timeInApp.displayMetric)
            : 60,
    )

    const handleConditionsChange = (change: Partial<MutableAppMessageConditions>) => {
        dispatch({ type: 'patch', entity: 'conditions', data: localValue.clone(change) })
    }

    const handleTimeInAppChange = (change: PropertiesOf<RelativeDate>) => {
        const timeInApp = RelativeDate.from(change as RelativeDate)
        handleConditionsChange({ timeInApp })
    }

    /**
     * Reset display options when type is updated, because discrepancy between options also default metric selected when not available
     */
    React.useEffect(() => {
        if (localValue.type === AppMessageConditionType.SESSION_TIME_IN_APP) {
            if (!SESSION_METRICS_FILTERED.includes(localValue.timeInApp!.displayMetric)) {
                handleTimeInAppChange({
                    ...localValue.timeInApp!,
                    displayMetric: RelativeDateDisplayMetric.MINUTES,
                })
            }
            setDisplayMetricOptions(SESSION_METRICS_FILTERED)
        } else if (localValue.type === AppMessageConditionType.LIFETIME_TIME_IN_APP) {
            setDisplayMetricOptions(LIFETIME_METRICS)
        }
    }, [value])

    return (
        <Well className={getClassNames('app-message-conditions-builder')} title="Conditions" hideFooter={true}>
            <Skeleton loading={context.loading} active={true} avatar={true} title={false}>
                <Form.Item
                    labelCol={{ style: { width: 65 } }}
                    labelAlign="left"
                    colon={false}
                    label="Displays"
                    className="conditions-builder-row-trigger-types"
                    htmlFor="amb-condition-trigger"
                >
                    <Space>
                        <Select<SessionTriggerTypes>
                            id="amb-condition-trigger"
                            style={{ width: 220 }}
                            defaultValue={
                                mapConditionToSessionTriggerType(localValue.type) ?? SessionTriggerTypes.IMMEDIATE
                            }
                            options={SESSION_TRIGGER_OPTIONS}
                            value={mapConditionToSessionTriggerType(localValue.type)}
                            disabled={context.disabled}
                            onChange={(triggerType) => {
                                let update: any = {}
                                switch (triggerType) {
                                    case SessionTriggerTypes.SESSION_START:
                                        update = {
                                            type: AppMessageConditionType.SESSION_START,
                                            timeInApp: {
                                                intervalSeconds: 60,
                                                displayMetric: RelativeDateDisplayMetric.SECONDS,
                                            },
                                        }
                                        break
                                    case SessionTriggerTypes.TIME_IN_APP:
                                        // Session type defaulted for time-in-app config below
                                        update = {
                                            type: AppMessageConditionType.SESSION_TIME_IN_APP,
                                            timeInApp: undefined,
                                        }

                                        break
                                    case SessionTriggerTypes.CUSTOM:
                                        update = {
                                            type: AppMessageConditionType.CUSTOM,
                                            timeInApp: undefined,
                                        }
                                        break
                                    case SessionTriggerTypes.IMMEDIATE:
                                        update = {
                                            type: AppMessageConditionType.IMMEDIATE,
                                            timeInApp: undefined,
                                        }
                                        break
                                }

                                if (update.type === AppMessageConditionType.SESSION_TIME_IN_APP) {
                                    update.timeInApp = {
                                        intervalSeconds: 60,
                                        displayMetric: RelativeDateDisplayMetric.SECONDS,
                                    }
                                } else if (update.type !== AppMessageConditionType.SESSION_TIME_IN_APP) {
                                    update.timeInApp = undefined
                                }

                                handleConditionsChange(update)
                            }}
                        />
                        <Popover
                            classNames={{
                                content: getClassNames('app-message-builder-popover'),
                            }}
                            content={
                                <div>
                                    {(Object.values(SessionTriggerTypes) as SessionTriggerTypes[]).map(
                                        (sessionTriggerType) => {
                                            const descriptions = SESSION_TRIGGER_DESCRIPTION_MAP[sessionTriggerType]

                                            return (
                                                <Typography.Paragraph key={sessionTriggerType}>
                                                    <Typography.Text strong>
                                                        {SESSION_TRIGGER_LABELS[sessionTriggerType]}
                                                    </Typography.Text>
                                                    : {descriptions.join(' ')}
                                                </Typography.Paragraph>
                                            )
                                        },
                                    )}
                                </div>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </Space>
                </Form.Item>
                {/*Config*/}
                {(value?.type === AppMessageConditionType.SESSION_TIME_IN_APP ||
                    value?.type === AppMessageConditionType.LIFETIME_TIME_IN_APP) && (
                    <Space className="conditions-builder-row-config time-in-app">
                        <span>When</span>
                        <Radio.Group
                            buttonStyle="solid"
                            disabled={context.disabled}
                            onChange={(ev) => {
                                const sessionType: string = ev.target.value
                                handleConditionsChange({
                                    type: AppMessageConditionType[sessionType + '_TIME_IN_APP'],
                                })
                            }}
                            size="small"
                            defaultValue={localValue.type?.split('_', 1)[0] ?? 'SESSION'}
                            options={Object.keys(ConditionDuration).map((type) => ({
                                label: titleCase(type),
                                value: ConditionDuration[type],
                            }))}
                            optionType="button"
                        />

                        <span>duration has reached</span>
                        <Space.Compact>
                            <label htmlFor="amb-time-in-app-value" className="sr-only">
                                Time in app duration value
                            </label>
                            <Input
                                id="amb-time-in-app-value"
                                type="number"
                                value={
                                    localValue.timeInApp && localValue.timeInApp.intervalSeconds
                                        ? plainTimeInAppSeconds ?? 1
                                        : undefined
                                }
                                defaultValue={30}
                                min={1}
                                max={365}
                                disabled={context.disabled}
                                onChange={(ev) => {
                                    const val = parseInt(ev.target.value.trim(), 10)
                                    const intervalSeconds = convertToSeconds(
                                        val,
                                        localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS,
                                    )
                                    setPlainTimeInAppSeconds(val)
                                    handleTimeInAppChange({
                                        intervalSeconds,
                                        displayMetric:
                                            localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS,
                                    })
                                }}
                            />
                            <label htmlFor="amb-time-in-app-metric" className="sr-only">
                                Time in app duration metric
                            </label>
                            <Select<RelativeDateDisplayMetric>
                                id="amb-time-in-app-metric"
                                defaultValue={RelativeDateDisplayMetric.SECONDS}
                                value={localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS}
                                options={displayMetricOptions.map((opt) => ({
                                    value: opt,
                                    label: titleCase(opt),
                                }))}
                                disabled={context.disabled}
                                onChange={(metric) => {
                                    handleTimeInAppChange({
                                        intervalSeconds: convertToSeconds(plainTimeInAppSeconds!, metric),
                                        displayMetric: metric,
                                    })
                                }}
                            />
                        </Space.Compact>
                    </Space>
                )}

                {value?.type === AppMessageConditionType.CUSTOM && (
                    <Form.Item
                        style={{ width: '100%' }}
                        className="conditions-builder-row-config custom"
                        extra={
                            <Typography.Paragraph className="font-size--s" type="secondary">
                                Custom Triggers are represented with an evaluating key and the qualitative value of that
                                key. Ex: Key: app_opened, Value: 5
                            </Typography.Paragraph>
                        }
                    >
                        <Space.Compact block>
                            <Space.Compact block>
                                <Space.Addon>
                                    <span>Key</span>
                                </Space.Addon>
                                <Input
                                    width="100%"
                                    value={localValue.triggers?.key}
                                    disabled={context.disabled}
                                    onChange={(ev) => {
                                        const key = ev.target.value.trim()
                                        handleConditionsChange({ triggers: { ...localValue.triggers, key } })
                                    }}
                                    placeholder="app_opened"
                                />
                            </Space.Compact>

                            <Space.Compact block>
                                <Space.Addon>
                                    <span>Value</span>
                                </Space.Addon>
                                <Input
                                    width="100%"
                                    value={localValue.triggers?.value}
                                    disabled={context.disabled}
                                    onChange={(ev) => {
                                        const val = ev.target.value.trim()
                                        handleConditionsChange({ triggers: { ...localValue.triggers, value: val } })
                                    }}
                                    placeholder="5"
                                />
                            </Space.Compact>
                        </Space.Compact>
                    </Form.Item>
                )}
            </Skeleton>
        </Well>
    )
}
